import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Select,
  SelectItem,
  Card,
  CardBody,
  Skeleton,
  Chip,
  Textarea,
  Badge,
  Tooltip,
  Switch,
  Divider,
  CheckboxGroup,
  Checkbox,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { Species } from 'common/src/utils/types';
import { supabase } from 'common/src/supabase';
import {
  getAllSpecies,
  addSpeciesGroup,
  renameSpeciesGroup,
  removeSpeciesGroup,
} from 'common/src/api/species';
import { VALID_FORMATS } from 'common/src/utils/definitions';
import {
  DEFAULT_MARKET_COMPARISON_CONFIG,
  type MarketComparisonConfig,
  type MatchVariable,
  type VariableGroups,
} from 'common/src/api/market-comparison-config';
import SpeciesBrandPaletteEditor from './SpeciesBrandPaletteEditor';
import { BRANDS } from 'common/src/utils/branding';
import {
  ConfirmModal,
  PageHeader,
  PageLayout,
} from '../../../components/ui';

// Converts a format type + weight into the weight_kg value used in market_data queries
function toWeightCondition(formatType: string, weight: number): string {
  const standardFormat = `${formatType}-${weight}KG`;
  if (standardFormat.includes('CAJA')) {
    return standardFormat.split('-')[1];
  } else if (standardFormat.includes('IN')) {
    return standardFormat.replace('-', '+');
  }
  return standardFormat;
}

function formatWeightLabel(weightCondition: string): string {
  if (weightCondition.includes('+')) {
    return weightCondition.replace('+', ' ');
  }
  return `CAJA ${weightCondition}`;
}

const VARIABLE_LABELS: Record<MatchVariable, string> = {
  variety: 'Variedad',
  format: 'Formato/Peso',
  caliber: 'Calibre',
  color: 'Color',
};

const SpeciesMarketComparisonConfig = ({
  config,
  onChange,
}: {
  config: MarketComparisonConfig | null;
  onChange: (config: MarketComparisonConfig) => void;
}) => {
  const current = config ?? DEFAULT_MARKET_COMPARISON_CONFIG;

  const updateField = <K extends keyof MarketComparisonConfig>(
    key: K,
    value: MarketComparisonConfig[K]
  ) => {
    onChange({ ...current, [key]: value });
  };

  const updateVariableGroups = (
    variableKey: keyof VariableGroups,
    groups: Record<string, string[]>
  ) => {
    onChange({
      ...current,
      variable_groups: {
        ...current.variable_groups,
        [variableKey]: Object.keys(groups).length > 0 ? groups : undefined,
      },
    });
  };

  // Generic group editor for variety, caliber, color
  const GenericGroupEditor = ({
    variableKey,
    label,
  }: {
    variableKey: keyof VariableGroups;
    label: string;
  }) => {
    const [name, setName] = useState('');
    const [values, setValues] = useState('');
    const groups = current.variable_groups?.[variableKey] ?? {};

    const handleAdd = () => {
      const trimmedName = name.trim();
      const parsed = values
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
      if (!trimmedName || parsed.length === 0) return;
      if (groups[trimmedName]) {
        toast.warning('Ya existe un grupo con ese nombre');
        return;
      }
      updateVariableGroups(variableKey, { ...groups, [trimmedName]: parsed });
      setName('');
      setValues('');
    };

    const handleDelete = (groupName: string) => {
      const updated = { ...groups };
      delete updated[groupName];
      updateVariableGroups(variableKey, updated);
    };

    return (
      <div className='mt-3'>
        <p className='text-sm font-semibold mb-2'>{label}</p>
        {Object.entries(groups).length > 0 && (
          <div className='space-y-2 mb-3'>
            {Object.entries(groups).map(([gName, gValues]) => (
              <div key={gName} className='flex items-center gap-2 flex-wrap'>
                <span className='text-sm font-medium min-w-[80px]'>
                  {gName}:
                </span>
                <div className='flex gap-1 flex-wrap'>
                  {gValues.map((v) => (
                    <Chip key={v} size='sm' variant='flat'>
                      {v}
                    </Chip>
                  ))}
                </div>
                <Button
                  size='sm'
                  color='danger'
                  variant='light'
                  isIconOnly
                  onClick={() => handleDelete(gName)}
                >
                  ✕
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className='flex gap-2 items-end'>
          <Input
            size='sm'
            label='Nombre del grupo'
            value={name}
            onValueChange={setName}
            className='max-w-[150px]'
          />
          <Input
            size='sm'
            label='Valores (separados por coma)'
            value={values}
            onValueChange={setValues}
            className='flex-1'
            placeholder='ej: 40, 42, 45'
          />
          <Button
            size='sm'
            variant='flat'
            onClick={handleAdd}
            isDisabled={!name.trim() || !values.trim()}
          >
            <Icon icon='tabler:plus' />
          </Button>
        </div>
      </div>
    );
  };

  // Format group editor with Select + weight input
  const FormatGroupEditor = () => {
    const [name, setName] = useState('');
    const [selectedFormat, setSelectedFormat] = useState('');
    const [weight, setWeight] = useState('');
    const [pending, setPending] = useState<string[]>([]);
    const groups = current.variable_groups?.format ?? {};

    const handleAddValue = () => {
      if (!selectedFormat || !weight) return;
      const wc = toWeightCondition(selectedFormat, parseFloat(weight));
      if (!pending.includes(wc)) {
        setPending([...pending, wc]);
      }
      setSelectedFormat('');
      setWeight('');
    };

    const handleAddGroup = () => {
      const trimmedName = name.trim();
      if (!trimmedName || pending.length === 0) return;
      if (groups[trimmedName]) {
        toast.warning('Ya existe un grupo con ese nombre');
        return;
      }
      updateVariableGroups('format', { ...groups, [trimmedName]: pending });
      setName('');
      setPending([]);
    };

    const handleDelete = (groupName: string) => {
      const updated = { ...groups };
      delete updated[groupName];
      updateVariableGroups('format', updated);
    };

    return (
      <div className='mt-3'>
        <p className='text-sm font-semibold mb-2'>Formato/Peso</p>
        {Object.entries(groups).length > 0 && (
          <div className='space-y-2 mb-3'>
            {Object.entries(groups).map(([gName, gValues]) => (
              <div key={gName} className='flex items-center gap-2 flex-wrap'>
                <span className='text-sm font-medium min-w-[80px]'>
                  {gName}:
                </span>
                <div className='flex gap-1 flex-wrap'>
                  {gValues.map((v) => (
                    <Chip key={v} size='sm' variant='flat'>
                      {formatWeightLabel(v)}
                    </Chip>
                  ))}
                </div>
                <Button
                  size='sm'
                  color='danger'
                  variant='light'
                  isIconOnly
                  onClick={() => handleDelete(gName)}
                >
                  ✕
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className='space-y-2'>
          <div className='flex gap-2 items-end'>
            <Input
              size='sm'
              label='Nombre del grupo'
              value={name}
              onValueChange={setName}
              className='max-w-[150px]'
            />
            <Select
              size='sm'
              label='Formato'
              selectedKeys={selectedFormat ? [selectedFormat] : []}
              onSelectionChange={(keys) => {
                const val = Array.from(keys)[0] as string;
                setSelectedFormat(val || '');
              }}
              className='max-w-[150px]'
            >
              {VALID_FORMATS.map((f) => (
                <SelectItem key={f}>{f}</SelectItem>
              ))}
            </Select>
            <Input
              size='sm'
              type='number'
              label='Peso (KG)'
              value={weight}
              onValueChange={setWeight}
              className='max-w-[120px]'
              min={0}
            />
            <Button
              size='sm'
              color='default'
              variant='flat'
              onClick={handleAddValue}
              isDisabled={!selectedFormat || !weight}
            >
              +
            </Button>
          </div>
          {pending.length > 0 && (
            <div className='flex gap-1 flex-wrap items-center'>
              <span className='text-sm text-default-500'>Valores:</span>
              {pending.map((v) => (
                <Chip
                  key={v}
                  size='sm'
                  variant='flat'
                  onClose={() => setPending(pending.filter((p) => p !== v))}
                >
                  {formatWeightLabel(v)}
                </Chip>
              ))}
            </div>
          )}
          <Button
            size='sm'
            variant='flat'
            onClick={handleAddGroup}
            isDisabled={!name.trim() || pending.length === 0}
          >
            Agregar grupo
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className='space-y-4'>
      <h4 className='font-medium'>Configuración Comparación de Mercado (Default)</h4>
      <p className='text-sm text-default-500'>
        Configuración por defecto para esta especie. Los exportadores pueden sobreescribirla con su propia configuración.
      </p>

      {/* Time Window */}
      <div>
        <p className='text-sm font-semibold mb-2'>Ventana de Tiempo</p>
        <div className='flex gap-4'>
          <Input
            type='number'
            size='sm'
            label='Días antes'
            value={String(current.window_days_before)}
            onValueChange={(v) =>
              updateField('window_days_before', Math.max(0, parseInt(v) || 0))
            }
            min={0}
            className='max-w-[150px]'
          />
          <Input
            type='number'
            size='sm'
            label='Días después'
            value={String(current.window_days_after)}
            onValueChange={(v) =>
              updateField('window_days_after', Math.max(0, parseInt(v) || 0))
            }
            min={0}
            className='max-w-[150px]'
          />
        </div>
      </div>

      {/* Match Variables */}
      <div>
        <p className='text-sm font-semibold mb-2'>Variables de Comparación</p>
        <CheckboxGroup
          value={current.match_variables}
          onValueChange={(values) =>
            updateField('match_variables', values as MatchVariable[])
          }
          orientation='horizontal'
          className='gap-4'
        >
          {(Object.entries(VARIABLE_LABELS) as [MatchVariable, string][]).map(
            ([key, label]) => (
              <Checkbox key={key} value={key}>
                {label}
              </Checkbox>
            )
          )}
        </CheckboxGroup>
      </div>

      {/* Variable Groups */}
      {current.match_variables.length > 0 && (
        <div>
          <p className='text-sm font-semibold mb-2'>Equivalencias al comparar</p>
          <p className='text-xs text-default-500 mb-2'>
            Valores dentro de una equivalencia se tratan como intercambiables
            al buscar precios de mercado (ej: calibres 2J y 3J juntos). No
            tiene relación con los "Grupos de la especie" de arriba.
          </p>

          {current.match_variables.includes('format') && <FormatGroupEditor />}

          {(
            Object.entries(VARIABLE_LABELS) as [MatchVariable, string][]
          ).map(([key, label]) => {
            if (key === 'format') return null;
            if (!current.match_variables.includes(key)) return null;
            return (
              <GenericGroupEditor key={key} variableKey={key} label={label} />
            );
          })}
        </div>
      )}
    </div>
  );
};

const SuperadminSpecies = () => {
  const [species, setSpecies] = useState<Species[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(null);
  const [newSpecies, setNewSpecies] = useState<{
    name: string;
    excel_name: string;
    varieties: string[];
    calibers: string[];
    colors: string[];
    has_color: boolean;
  }>({
    name: '',
    excel_name: '',
    varieties: [],
    calibers: [],
    colors: [],
    has_color: false,
  });

  // Estado para manejar la entrada de nueva variedad
  const [newVariety, setNewVariety] = useState('');
  const [editingVariety, setEditingVariety] = useState({
    index: -1,
    value: '',
  });

  // Estado para manejar la entrada de nueva all_variety
  const [newAllVariety, setNewAllVariety] = useState('');
  const [editingAllVariety, setEditingAllVariety] = useState({
    index: -1,
    value: '',
  });

  // Estado para manejar la entrada de nuevo calibre
  const [newCaliber, setNewCaliber] = useState('');
  const [editingCaliber, setEditingCaliber] = useState({
    index: -1,
    value: '',
  });

  // Estado para manejar la entrada de nuevo color
  const [newColor, setNewColor] = useState('');
  const [editingColor, setEditingColor] = useState({
    index: -1,
    value: '',
  });

  // Estado para manejar la entrada de nuevo grupo
  const [newGroup, setNewGroup] = useState('');
  const [editingGroup, setEditingGroup] = useState({
    index: -1,
    value: '',
  });

  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const {
    isOpen: isNewOpen,
    onOpen: onNewOpen,
    onClose: onNewClose,
  } = useDisclosure();

  const fetchSpecies = async () => {
    setLoading(true);
    try {
      const fetchedSpecies = await getAllSpecies();
      setSpecies(fetchedSpecies);
    } catch (error) {
      console.error('Error fetching species:', error);
      toast.error('Failed to fetch species');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpecies();
  }, []);

  const handleEdit = (species: Species) => {
    // Asegurarse de que varieties, calibers y colors son arrays
    const speciesWithArrays = {
      ...species,
      varieties: Array.isArray(species.varieties) ? species.varieties : [],
      all_varieties: Array.isArray(species.all_varieties) ? species.all_varieties : [],
      calibers: Array.isArray(species.calibers) ? species.calibers : [],
      colors: Array.isArray(species.colors) ? species.colors : [],
      groups: Array.isArray(species.groups) ? species.groups : [],
    };
    setSelectedSpecies(speciesWithArrays);
    onEditOpen();
  };

  const handleDelete = (species: Species) => {
    setSelectedSpecies(species);
    onDeleteOpen();
  };

  // Funciones para gestionar variedades en especies existentes
  const addVarietyToSelected = () => {
    if (!newVariety.trim() || !selectedSpecies) return;

    // Verificar que la variedad no exista ya
    if (selectedSpecies.varieties.includes(newVariety.trim())) {
      toast.warning('Esta variedad ya existe');
      return;
    }

    setSelectedSpecies({
      ...selectedSpecies,
      varieties: [...selectedSpecies.varieties, newVariety.trim()],
    });
    setNewVariety('');
  };

  const removeVarietyFromSelected = (index: number) => {
    if (!selectedSpecies) return;

    const updatedVarieties = [...selectedSpecies.varieties];
    updatedVarieties.splice(index, 1);

    setSelectedSpecies({
      ...selectedSpecies,
      varieties: updatedVarieties,
    });
  };

  const startEditVariety = (index: number) => {
    if (!selectedSpecies) return;
    setEditingVariety({
      index,
      value: selectedSpecies.varieties[index],
    });
  };

  const saveEditedVariety = () => {
    if (!selectedSpecies || editingVariety.index === -1) return;

    // Verificar que la variedad no exista ya
    if (
      selectedSpecies.varieties.includes(editingVariety.value.trim()) &&
      selectedSpecies.varieties[editingVariety.index] !==
        editingVariety.value.trim()
    ) {
      toast.warning('Esta variedad ya existe');
      return;
    }

    const updatedVarieties = [...selectedSpecies.varieties];
    updatedVarieties[editingVariety.index] = editingVariety.value.trim();

    setSelectedSpecies({
      ...selectedSpecies,
      varieties: updatedVarieties,
    });

    setEditingVariety({ index: -1, value: '' });
  };

  const moveVariety = (index: number, direction: 'up' | 'down') => {
    if (!selectedSpecies) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= selectedSpecies.varieties.length) return;

    const updatedVarieties = [...selectedSpecies.varieties];
    [updatedVarieties[index], updatedVarieties[newIndex]] = [
      updatedVarieties[newIndex],
      updatedVarieties[index],
    ];

    setSelectedSpecies({
      ...selectedSpecies,
      varieties: updatedVarieties,
    });
  };

  // Funciones para gestionar all_varieties en especies existentes
  const addAllVarietyToSelected = () => {
    if (!newAllVariety.trim() || !selectedSpecies) return;
    const allVars = selectedSpecies.all_varieties || [];
    if (allVars.includes(newAllVariety.trim())) {
      toast.warning('Esta variedad ya existe');
      return;
    }
    setSelectedSpecies({
      ...selectedSpecies,
      all_varieties: [...allVars, newAllVariety.trim()],
    });
    setNewAllVariety('');
  };

  const removeAllVarietyFromSelected = (index: number) => {
    if (!selectedSpecies) return;
    const updated = [...(selectedSpecies.all_varieties || [])];
    updated.splice(index, 1);
    setSelectedSpecies({ ...selectedSpecies, all_varieties: updated });
  };

  const startEditAllVariety = (index: number) => {
    const allVars = selectedSpecies?.all_varieties || [];
    setEditingAllVariety({ index, value: allVars[index] });
  };

  const saveEditedAllVariety = () => {
    if (!selectedSpecies || editingAllVariety.index === -1) return;
    const updated = [...(selectedSpecies.all_varieties || [])];
    updated[editingAllVariety.index] = editingAllVariety.value.trim();
    setSelectedSpecies({ ...selectedSpecies, all_varieties: updated });
    setEditingAllVariety({ index: -1, value: '' });
  };

  const moveAllVariety = (index: number, direction: 'up' | 'down') => {
    if (!selectedSpecies) return;
    const updated = [...(selectedSpecies.all_varieties || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= updated.length) return;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setSelectedSpecies({ ...selectedSpecies, all_varieties: updated });
  };

  // Funciones para gestionar calibres en especies existentes
  const addCaliberToSelected = () => {
    if (!newCaliber.trim() || !selectedSpecies) return;

    // Verificar que el calibre no exista ya
    if (selectedSpecies.calibers?.includes(newCaliber.trim())) {
      toast.warning('Este calibre ya existe');
      return;
    }

    setSelectedSpecies({
      ...selectedSpecies,
      calibers: [...(selectedSpecies.calibers || []), newCaliber.trim()],
    });
    setNewCaliber('');
  };

  const removeCaliberFromSelected = (index: number) => {
    if (!selectedSpecies || !selectedSpecies.calibers) return;

    const updatedCalibers = [...selectedSpecies.calibers];
    updatedCalibers.splice(index, 1);

    setSelectedSpecies({
      ...selectedSpecies,
      calibers: updatedCalibers,
    });
  };

  const startEditCaliber = (index: number) => {
    if (!selectedSpecies || !selectedSpecies.calibers) return;
    setEditingCaliber({
      index,
      value: selectedSpecies.calibers[index],
    });
  };

  const saveEditedCaliber = () => {
    if (!selectedSpecies || !selectedSpecies.calibers || editingCaliber.index === -1) return;

    // Verificar que el calibre no exista ya
    if (
      selectedSpecies.calibers.includes(editingCaliber.value.trim()) &&
      selectedSpecies.calibers[editingCaliber.index] !==
        editingCaliber.value.trim()
    ) {
      toast.warning('Este calibre ya existe');
      return;
    }

    const updatedCalibers = [...selectedSpecies.calibers];
    updatedCalibers[editingCaliber.index] = editingCaliber.value.trim();

    setSelectedSpecies({
      ...selectedSpecies,
      calibers: updatedCalibers,
    });

    setEditingCaliber({ index: -1, value: '' });
  };

  const moveCaliber = (index: number, direction: 'up' | 'down') => {
    if (!selectedSpecies || !selectedSpecies.calibers) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= selectedSpecies.calibers.length) return;

    const updatedCalibers = [...selectedSpecies.calibers];
    [updatedCalibers[index], updatedCalibers[newIndex]] = [
      updatedCalibers[newIndex],
      updatedCalibers[index],
    ];

    setSelectedSpecies({
      ...selectedSpecies,
      calibers: updatedCalibers,
    });
  };

  // Funciones para gestionar colores en especies existentes
  const addColorToSelected = () => {
    if (!newColor.trim() || !selectedSpecies) return;

    // Verificar que el color no exista ya
    if (selectedSpecies.colors?.includes(newColor.trim())) {
      toast.warning('Este color ya existe');
      return;
    }

    setSelectedSpecies({
      ...selectedSpecies,
      colors: [...(selectedSpecies.colors || []), newColor.trim()],
    });
    setNewColor('');
  };

  const removeColorFromSelected = (index: number) => {
    if (!selectedSpecies || !selectedSpecies.colors) return;

    const updatedColors = [...selectedSpecies.colors];
    updatedColors.splice(index, 1);

    setSelectedSpecies({
      ...selectedSpecies,
      colors: updatedColors,
    });
  };

  const startEditColor = (index: number) => {
    if (!selectedSpecies || !selectedSpecies.colors) return;
    setEditingColor({
      index,
      value: selectedSpecies.colors[index],
    });
  };

  const saveEditedColor = () => {
    if (!selectedSpecies || !selectedSpecies.colors || editingColor.index === -1) return;

    // Verificar que el color no exista ya
    if (
      selectedSpecies.colors.includes(editingColor.value.trim()) &&
      selectedSpecies.colors[editingColor.index] !==
        editingColor.value.trim()
    ) {
      toast.warning('Este color ya existe');
      return;
    }

    const updatedColors = [...selectedSpecies.colors];
    updatedColors[editingColor.index] = editingColor.value.trim();

    setSelectedSpecies({
      ...selectedSpecies,
      colors: updatedColors,
    });

    setEditingColor({ index: -1, value: '' });
  };

  const moveColor = (index: number, direction: 'up' | 'down') => {
    if (!selectedSpecies || !selectedSpecies.colors) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= selectedSpecies.colors.length) return;

    const updatedColors = [...selectedSpecies.colors];
    [updatedColors[index], updatedColors[newIndex]] = [
      updatedColors[newIndex],
      updatedColors[index],
    ];

    setSelectedSpecies({
      ...selectedSpecies,
      colors: updatedColors,
    });
  };

  // Gestión de grupos en especies existentes.
  //
  // A diferencia de variedades/calibres/colores, los grupos NO se editan en
  // estado local para guardarse después con el resto del formulario: van por
  // RPC y se aplican de inmediato. El motivo es que renombrar o eliminar un
  // grupo tiene que arrastrar `retail_target_skus.species_group` en la misma
  // transacción; si no, los SKUs quedan apuntando a un grupo inexistente y se
  // muestran como "sin grupo" aunque en la base conserven el valor viejo.
  // Ver migración 20260803120000_species_group_rpcs.sql.
  const addGroupToSelected = async () => {
    if (!newGroup.trim() || !selectedSpecies) return;

    try {
      const groups = await addSpeciesGroup(selectedSpecies.id, newGroup);
      setSelectedSpecies({ ...selectedSpecies, groups });
      setNewGroup('');
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'No se pudo crear el grupo'
      );
    }
  };

  const removeGroupFromSelected = async (index: number) => {
    if (!selectedSpecies || !selectedSpecies.groups) return;
    const group = selectedSpecies.groups[index];
    if (!group) return;

    try {
      const groups = await removeSpeciesGroup(selectedSpecies.id, group);
      setSelectedSpecies({ ...selectedSpecies, groups });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'No se pudo eliminar el grupo'
      );
    }
  };

  const startEditGroup = (index: number) => {
    if (!selectedSpecies || !selectedSpecies.groups) return;
    setEditingGroup({
      index,
      value: selectedSpecies.groups[index],
    });
  };

  const saveEditedGroup = async () => {
    if (!selectedSpecies || !selectedSpecies.groups || editingGroup.index === -1)
      return;

    const oldGroup = selectedSpecies.groups[editingGroup.index];
    if (!oldGroup) return;

    try {
      // La RPC renombra el grupo y actualiza los SKUs que lo usaban.
      const groups = await renameSpeciesGroup(
        selectedSpecies.id,
        oldGroup,
        editingGroup.value
      );
      setSelectedSpecies({ ...selectedSpecies, groups });
      setEditingGroup({ index: -1, value: '' });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'No se pudo renombrar el grupo'
      );
    }
  };

  const moveGroup = (index: number, direction: 'up' | 'down') => {
    if (!selectedSpecies || !selectedSpecies.groups) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= selectedSpecies.groups.length) return;

    const updatedGroups = [...selectedSpecies.groups];
    [updatedGroups[index], updatedGroups[newIndex]] = [
      updatedGroups[newIndex],
      updatedGroups[index],
    ];

    setSelectedSpecies({
      ...selectedSpecies,
      groups: updatedGroups,
    });
  };

  // Funciones para gestionar variedades en nuevas especies
  const addVarietyToNew = () => {
    if (!newVariety.trim()) return;

    // Verificar que la variedad no exista ya
    if (newSpecies.varieties.includes(newVariety.trim())) {
      toast.warning('Esta variedad ya existe');
      return;
    }

    setNewSpecies({
      ...newSpecies,
      varieties: [...newSpecies.varieties, newVariety.trim()],
    });
    setNewVariety('');
  };

  const removeVarietyFromNew = (index: number) => {
    const updatedVarieties = [...newSpecies.varieties];
    updatedVarieties.splice(index, 1);

    setNewSpecies({
      ...newSpecies,
      varieties: updatedVarieties,
    });
  };

  const moveNewVariety = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newSpecies.varieties.length) return;

    const updatedVarieties = [...newSpecies.varieties];
    [updatedVarieties[index], updatedVarieties[newIndex]] = [
      updatedVarieties[newIndex],
      updatedVarieties[index],
    ];

    setNewSpecies({
      ...newSpecies,
      varieties: updatedVarieties,
    });
  };

  // Funciones para gestionar calibres en nuevas especies
  const addCaliberToNew = () => {
    if (!newCaliber.trim()) return;

    // Verificar que el calibre no exista ya
    if (newSpecies.calibers.includes(newCaliber.trim())) {
      toast.warning('Este calibre ya existe');
      return;
    }

    setNewSpecies({
      ...newSpecies,
      calibers: [...newSpecies.calibers, newCaliber.trim()],
    });
    setNewCaliber('');
  };

  const removeCaliberFromNew = (index: number) => {
    const updatedCalibers = [...newSpecies.calibers];
    updatedCalibers.splice(index, 1);

    setNewSpecies({
      ...newSpecies,
      calibers: updatedCalibers,
    });
  };

  const moveNewCaliber = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newSpecies.calibers.length) return;

    const updatedCalibers = [...newSpecies.calibers];
    [updatedCalibers[index], updatedCalibers[newIndex]] = [
      updatedCalibers[newIndex],
      updatedCalibers[index],
    ];

    setNewSpecies({
      ...newSpecies,
      calibers: updatedCalibers,
    });
  };

  // Funciones para gestionar colores en nuevas especies
  const addColorToNew = () => {
    if (!newColor.trim()) return;

    // Verificar que el color no exista ya
    if (newSpecies.colors.includes(newColor.trim())) {
      toast.warning('Este color ya existe');
      return;
    }

    setNewSpecies({
      ...newSpecies,
      colors: [...newSpecies.colors, newColor.trim()],
    });
    setNewColor('');
  };

  const removeColorFromNew = (index: number) => {
    const updatedColors = [...newSpecies.colors];
    updatedColors.splice(index, 1);

    setNewSpecies({
      ...newSpecies,
      colors: updatedColors,
    });
  };

  const moveNewColor = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newSpecies.colors.length) return;

    const updatedColors = [...newSpecies.colors];
    [updatedColors[index], updatedColors[newIndex]] = [
      updatedColors[newIndex],
      updatedColors[index],
    ];

    setNewSpecies({
      ...newSpecies,
      colors: updatedColors,
    });
  };

  const handleEditSubmit = async () => {
    if (!selectedSpecies) return;
    try {
      const { error } = await supabase
        .from('species')
        .update({
          name: selectedSpecies.name,
          excel_name: selectedSpecies.excel_name,
          varieties: selectedSpecies.varieties,
          all_varieties: selectedSpecies.all_varieties?.length ? selectedSpecies.all_varieties : null,
          calibers: selectedSpecies.calibers || null,
          colors: selectedSpecies.colors?.length ? selectedSpecies.colors : null,
          has_color: selectedSpecies.has_color,
          groups: selectedSpecies.groups || [],
          market_comparison_config: selectedSpecies.market_comparison_config || null,
          brand_color_light: selectedSpecies.brand_color_light ?? null,
          brand_color_dark: selectedSpecies.brand_color_dark ?? null,
        })
        .eq('id', selectedSpecies.id);

      if (error) throw error;

      toast.success('Species updated successfully');
      onEditClose();
      fetchSpecies();
    } catch (error) {
      console.error('Error updating species:', error);
      toast.error('Failed to update species');
    }
  };

  const handleNewSubmit = async () => {
    try {
      const { error } = await supabase.from('species').insert([
        {
          name: newSpecies.name,
          excel_name: newSpecies.excel_name,
          varieties: newSpecies.varieties,
          calibers: newSpecies.calibers.length > 0 ? newSpecies.calibers : null,
          colors: newSpecies.colors.length > 0 ? newSpecies.colors : null,
          has_color: newSpecies.has_color,
        },
      ]);

      if (error) throw error;

      toast.success('New species created successfully');
      onNewClose();
      setNewSpecies({
        name: '',
        excel_name: '',
        varieties: [],
        calibers: [],
        colors: [],
        has_color: false,
      });
      fetchSpecies();
    } catch (error) {
      console.error('Error creating species:', error);
      toast.error('Failed to create new species');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSpecies) return;
    try {
      // Verificar si la especie está siendo usada por algún exportador
      const { data: relationData, error: relationError } = await supabase
        .from('exporters_species')
        .select('id')
        .eq('species_id', selectedSpecies.id);

      if (relationError) throw relationError;

      if (relationData && relationData.length > 0) {
        toast.error(
          'Cannot delete this species because it is associated with exporters'
        );
        onDeleteClose();
        return;
      }

      const { error } = await supabase
        .from('species')
        .delete()
        .eq('id', selectedSpecies.id);

      if (error) throw error;

      toast.success('Species deleted successfully');
      onDeleteClose();
      fetchSpecies();
    } catch (error) {
      console.error('Error deleting species:', error);
      toast.error('Failed to delete species');
    }
  };

  const columns = [
    { name: 'ID', uid: 'id' },
    { name: 'Nombre', uid: 'name' },
    { name: 'Nombre en Excel', uid: 'excel_name' },
    { name: 'Tiene Color', uid: 'has_color' },
    { name: 'Marca', uid: 'brand_palette' },
    { name: 'Variedades', uid: 'varieties' },
    { name: 'Calibres', uid: 'calibers' },
    { name: 'Colores', uid: 'colors' },
    { name: 'Grupos especie', uid: 'groups' },
    { name: 'Acciones', uid: 'actions' },
  ];

  const renderCell = (species: Species, columnKey: React.Key) => {
    switch (columnKey) {
      case 'has_color':
        return (
          <Chip
            variant='flat'
            color={species.has_color ? 'success' : 'default'}
          >
            {species.has_color ? 'Sí' : 'No'}
          </Chip>
        );
      case 'brand_palette': {
        // Show a 700-stop swatch per brand defined for this species (light mode).
        const branded = species.brand_color_light as
          | Record<string, Record<string, string>>
          | null
          | undefined;
        const brands = Object.keys(BRANDS) as Array<keyof typeof BRANDS>;
        const swatches = brands
          .map((brand) => {
            const palette = branded?.[brand] ?? branded?.cherry;
            const hex = palette?.['700'];
            return hex ? { brand, hex } : null;
          })
          .filter((x): x is { brand: keyof typeof BRANDS; hex: string } => x !== null);
        if (swatches.length === 0) {
          return <span className='text-default-400'>-</span>;
        }
        return (
          <div className='flex items-center gap-1.5'>
            {swatches.map(({ brand, hex }) => (
              <Tooltip
                key={brand}
                content={`${BRANDS[brand].name} · ${hex}`}
              >
                <span
                  className='inline-block h-5 w-5 rounded-full border border-default-200 dark:border-default-100'
                  style={{ backgroundColor: hex }}
                />
              </Tooltip>
            ))}
          </div>
        );
      }
      case 'varieties':
        return (
          <div className='flex flex-wrap gap-1'>
            {Array.isArray(species.varieties) &&
            species.varieties.length > 0 ? (
              species.varieties.map((variety, idx) => (
                <Badge key={idx} variant='flat' color='primary'>
                  {variety}
                </Badge>
              ))
            ) : (
              <span className='text-default-400'>No varieties</span>
            )}
          </div>
        );
      case 'calibers':
        return (
          <div className='flex flex-wrap gap-1'>
            {Array.isArray(species.calibers) &&
            species.calibers.length > 0 ? (
              species.calibers.map((caliber, idx) => (
                <Badge key={idx} variant='flat' color='secondary'>
                  {caliber}
                </Badge>
              ))
            ) : (
              <span className='text-default-400'>Todos</span>
            )}
          </div>
        );
      case 'colors':
        return (
          <div className='flex flex-wrap gap-1'>
            {Array.isArray(species.colors) &&
            species.colors.length > 0 ? (
              species.colors.map((color, idx) => (
                <Badge key={idx} variant='flat' color='warning'>
                  {color}
                </Badge>
              ))
            ) : (
              <span className='text-default-400'>-</span>
            )}
          </div>
        );
      case 'groups':
        return (
          <div className='flex flex-wrap gap-1'>
            {Array.isArray(species.groups) && species.groups.length > 0 ? (
              species.groups.map((group, idx) => (
                <Badge key={idx} variant='flat' color='success'>
                  {group}
                </Badge>
              ))
            ) : (
              <span className='text-default-400'>-</span>
            )}
          </div>
        );
      case 'actions':
        return (
          <div className='flex items-center gap-2'>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              onClick={() => handleEdit(species)}
            >
              <Icon icon='tabler:edit' className='text-default-400' />
            </Button>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              color='danger'
              onClick={() => handleDelete(species)}
            >
              <Icon icon='tabler:trash' className='text-danger-400' />
            </Button>
          </div>
        );
      default:
        return String(species[columnKey as keyof Species] || '');
    }
  };

  // Componente para renderizar las variedades con opciones de edición
  const VarietiesList = ({
    varieties,
    onRemove,
    onMove,
    isEditingVariety = false,
    onEdit,
  }: {
    varieties: string[];
    onRemove: (index: number) => void;
    onMove: (index: number, direction: 'up' | 'down') => void;
    isEditingVariety?: boolean;
    onEdit?: (index: number) => void;
  }) => {
    return (
      <div className='space-y-2 max-h-72 overflow-y-auto p-2 border border-default-100 rounded-lg'>
        {varieties.length === 0 ? (
          <p className='text-default-500'>No hay variedades. Agrega una nueva.</p>
        ) : (
          varieties.map((variety, index) => (
            <div
              key={index}
              className='flex items-center gap-2 p-2 bg-default-100 rounded'
            >
              <span className='flex-grow'>{variety}</span>
              <div className='flex gap-1'>
                {isEditingVariety && onEdit && (
                  <Button
                    isIconOnly
                    size='sm'
                    variant='light'
                    onClick={() => onEdit(index)}
                  >
                    <Icon icon='tabler:edit' className='text-blue-500' />
                  </Button>
                )}
                <Button
                  isIconOnly
                  size='sm'
                  variant='light'
                  onClick={() => onMove(index, 'up')}
                  isDisabled={index === 0}
                >
                  <Icon icon='tabler:arrow-up' className='text-default-500' />
                </Button>
                <Button
                  isIconOnly
                  size='sm'
                  variant='light'
                  onClick={() => onMove(index, 'down')}
                  isDisabled={index === varieties.length - 1}
                >
                  <Icon icon='tabler:arrow-down' className='text-default-500' />
                </Button>
                <Button
                  isIconOnly
                  size='sm'
                  variant='light'
                  color='danger'
                  onClick={() => onRemove(index)}
                >
                  <Icon icon='tabler:trash' className='text-danger-400' />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <PageLayout>
      <PageHeader
        title='Especies'
        actions={
          <Button color='primary' onClick={onNewOpen}>
            Añadir Especie
          </Button>
        }
      />

      {loading ? (
        <div className=''>
          {/* Title and Button Skeleton */}
          <div className='flex justify-between items-center '>
            <div className='space-y-2'>
              <Skeleton className='w-1/3 rounded-lg' />
            </div>
          </div>

          {/* Table Skeleton */}
          <Card className='w-full'>
            <CardBody>
              <div className='space-y-4'>
                {/* Header Row */}
                <div className='grid grid-cols-7 gap-4'>
                  {[...Array(7)].map((_, i) => (
                    <Skeleton key={i} className='h-8 w-full rounded-lg' />
                  ))}
                </div>

                {/* Table Rows */}
                {[...Array(5)].map((_, rowIndex) => (
                  <div key={rowIndex} className='grid grid-cols-7 gap-4'>
                    <Skeleton className='h-12 w-16 rounded-lg' />
                    <Skeleton className='h-12 w-full rounded-lg' />
                    <Skeleton className='h-12 w-full rounded-lg' />
                    <Skeleton className='h-12 w-full rounded-lg' />
                    <Skeleton className='h-12 w-full rounded-lg' />
                    <Skeleton className='h-12 w-full rounded-lg' />
                    <div className='flex gap-2 justify-center'>
                      <Skeleton className='h-8 w-8 rounded-lg' />
                      <Skeleton className='h-8 w-8 rounded-lg' />
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </div>
      ) : (
        <Table removeWrapper aria-label='Species table'>
          <TableHeader columns={columns}>
            {(column) => (
              <TableColumn key={column.uid}>{column.name}</TableColumn>
            )}
          </TableHeader>
          <TableBody items={species}>
            {(item) => (
              <TableRow key={item.id}>
                {(columnKey) => (
                  <TableCell>{renderCell(item, columnKey)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {/* Edit Modal */}
      <Modal
        isOpen={isEditOpen}
        onClose={onEditClose}
        size='5xl'
        scrollBehavior='inside'
      >
        <ModalContent>
          <ModalHeader>Editar Especie</ModalHeader>
          <ModalBody className='max-h-[75vh] overflow-y-auto'>
            <div className='space-y-4'>
              <Input
                label='Nombre'
                value={selectedSpecies?.name || ''}
                onChange={(e) =>
                  setSelectedSpecies({
                    ...selectedSpecies!,
                    name: e.target.value,
                  })
                }
              />
              <Input
                label='Nombre en Excel'
                value={selectedSpecies?.excel_name || ''}
                onChange={(e) =>
                  setSelectedSpecies({
                    ...selectedSpecies!,
                    excel_name: e.target.value,
                  })
                }
              />

              <div className='flex items-center gap-2'>
                <Switch
                  isSelected={selectedSpecies?.has_color || false}
                  onValueChange={(value) =>
                    setSelectedSpecies({
                      ...selectedSpecies!,
                      has_color: value,
                    })
                  }
                />
                <span>Tiene color</span>
              </div>

              <div className='grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-4'>
              <div className='space-y-2'>
                <h4 className='font-medium'>Variedades</h4>
                <p className='text-sm text-default-500'>
                  El orden de las variedades es importante.
                </p>

                {editingVariety.index !== -1 ? (
                  <div className='flex gap-2'>
                    <Input
                      fullWidth
                      value={editingVariety.value}
                      onChange={(e) =>
                        setEditingVariety({
                          ...editingVariety,
                          value: e.target.value,
                        })
                      }
                      placeholder='Editar variedad'
                    />
                    <Button color='primary' onClick={saveEditedVariety}>
                      Guardar
                    </Button>
                    <Button
                      color='danger'
                      variant='light'
                      onClick={() =>
                        setEditingVariety({ index: -1, value: '' })
                      }
                    >
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <div className='flex gap-2'>
                    <Input
                      fullWidth
                      value={newVariety}
                      onChange={(e) => setNewVariety(e.target.value)}
                      placeholder='Nueva variedad'
                    />
                    <Button color='primary' onClick={addVarietyToSelected}>
                      <Icon icon='tabler:plus' />
                    </Button>
                  </div>
                )}

                <VarietiesList
                  varieties={selectedSpecies?.varieties || []}
                  onRemove={removeVarietyFromSelected}
                  onMove={moveVariety}
                  isEditingVariety={true}
                  onEdit={startEditVariety}
                />
              </div>

              <div className='space-y-2'>
                <h4 className='font-medium'>Todas las Variedades</h4>
                <p className='text-sm text-default-500'>
                  Lista completa de variedades (incluye variantes y sinónimos). Se usa para validar datos de mercado.
                </p>

                {editingAllVariety.index !== -1 ? (
                  <div className='flex gap-2'>
                    <Input
                      fullWidth
                      value={editingAllVariety.value}
                      onChange={(e) =>
                        setEditingAllVariety({
                          ...editingAllVariety,
                          value: e.target.value,
                        })
                      }
                      placeholder='Editar variedad'
                    />
                    <Button color='primary' onClick={saveEditedAllVariety}>
                      Guardar
                    </Button>
                    <Button
                      color='danger'
                      variant='light'
                      onClick={() =>
                        setEditingAllVariety({ index: -1, value: '' })
                      }
                    >
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <div className='flex gap-2'>
                    <Input
                      fullWidth
                      value={newAllVariety}
                      onChange={(e) => setNewAllVariety(e.target.value)}
                      placeholder='Nueva variedad'
                    />
                    <Button color='primary' onClick={addAllVarietyToSelected}>
                      <Icon icon='tabler:plus' />
                    </Button>
                  </div>
                )}

                <VarietiesList
                  varieties={selectedSpecies?.all_varieties || []}
                  onRemove={removeAllVarietyFromSelected}
                  onMove={moveAllVariety}
                  isEditingVariety={true}
                  onEdit={startEditAllVariety}
                />
              </div>

              <div className='space-y-2'>
                <h4 className='font-medium'>Calibres</h4>
                <p className='text-sm text-default-500'>
                  El orden de los calibres es importante. Dejar vacío para permitir todos los calibres.
                </p>

                {editingCaliber.index !== -1 ? (
                  <div className='flex gap-2'>
                    <Input
                      fullWidth
                      value={editingCaliber.value}
                      onChange={(e) =>
                        setEditingCaliber({
                          ...editingCaliber,
                          value: e.target.value,
                        })
                      }
                      placeholder='Editar calibre'
                    />
                    <Button color='primary' onClick={saveEditedCaliber}>
                      Guardar
                    </Button>
                    <Button
                      color='danger'
                      variant='light'
                      onClick={() =>
                        setEditingCaliber({ index: -1, value: '' })
                      }
                    >
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <div className='flex gap-2'>
                    <Input
                      fullWidth
                      value={newCaliber}
                      onChange={(e) => setNewCaliber(e.target.value)}
                      placeholder='Nuevo calibre'
                    />
                    <Button color='primary' onClick={addCaliberToSelected}>
                      <Icon icon='tabler:plus' />
                    </Button>
                  </div>
                )}

                <VarietiesList
                  varieties={selectedSpecies?.calibers || []}
                  onRemove={removeCaliberFromSelected}
                  onMove={moveCaliber}
                  isEditingVariety={true}
                  onEdit={startEditCaliber}
                />
              </div>

              <div className='space-y-2'>
                <h4 className='font-medium'>Colores</h4>
                <p className='text-sm text-default-500'>
                  El orden de los colores es importante. Dejar vacío si no aplica.
                </p>

                {editingColor.index !== -1 ? (
                  <div className='flex gap-2'>
                    <Input
                      fullWidth
                      value={editingColor.value}
                      onChange={(e) =>
                        setEditingColor({
                          ...editingColor,
                          value: e.target.value,
                        })
                      }
                      placeholder='Editar color'
                    />
                    <Button color='primary' onClick={saveEditedColor}>
                      Guardar
                    </Button>
                    <Button
                      color='danger'
                      variant='light'
                      onClick={() =>
                        setEditingColor({ index: -1, value: '' })
                      }
                    >
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <div className='flex gap-2'>
                    <Input
                      fullWidth
                      value={newColor}
                      onChange={(e) => setNewColor(e.target.value)}
                      placeholder='Nuevo color'
                    />
                    <Button color='primary' onClick={addColorToSelected}>
                      <Icon icon='tabler:plus' />
                    </Button>
                  </div>
                )}

                <VarietiesList
                  varieties={selectedSpecies?.colors || []}
                  onRemove={removeColorFromSelected}
                  onMove={moveColor}
                  isEditingVariety={true}
                  onEdit={startEditColor}
                />
              </div>

              <div className='space-y-2'>
                <h4 className='font-medium'>Grupos de la especie</h4>
                <p className='text-sm text-default-500'>
                  Tags a criterio del equipo (ej: Reds, Blacks). Cada SKU de
                  retail puede asignarse a uno de estos grupos. No confundir
                  con las equivalencias de comparación de mercado, más abajo.
                </p>

                {editingGroup.index !== -1 ? (
                  <div className='flex gap-2'>
                    <Input
                      fullWidth
                      value={editingGroup.value}
                      onChange={(e) =>
                        setEditingGroup({
                          ...editingGroup,
                          value: e.target.value,
                        })
                      }
                      placeholder='Editar grupo'
                    />
                    <Button color='primary' onClick={saveEditedGroup}>
                      Guardar
                    </Button>
                    <Button
                      color='danger'
                      variant='light'
                      onClick={() =>
                        setEditingGroup({ index: -1, value: '' })
                      }
                    >
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <div className='flex gap-2'>
                    <Input
                      fullWidth
                      value={newGroup}
                      onChange={(e) => setNewGroup(e.target.value)}
                      placeholder='Nuevo grupo'
                    />
                    <Button color='primary' onClick={addGroupToSelected}>
                      <Icon icon='tabler:plus' />
                    </Button>
                  </div>
                )}

                <VarietiesList
                  varieties={selectedSpecies?.groups || []}
                  onRemove={removeGroupFromSelected}
                  onMove={moveGroup}
                  isEditingVariety={true}
                  onEdit={startEditGroup}
                />
              </div>
              </div>

              <Divider className='my-2' />

              <SpeciesBrandPaletteEditor
                light={selectedSpecies?.brand_color_light as any}
                dark={selectedSpecies?.brand_color_dark as any}
                onChange={({ light, dark }) => {
                  if (!selectedSpecies) return;
                  setSelectedSpecies({
                    ...selectedSpecies,
                    brand_color_light: light,
                    brand_color_dark: dark,
                  });
                }}
              />

              <Divider className='my-2' />

              <SpeciesMarketComparisonConfig
                config={
                  (selectedSpecies?.market_comparison_config as MarketComparisonConfig) ??
                  null
                }
                onChange={(config) => {
                  if (!selectedSpecies) return;
                  setSelectedSpecies({
                    ...selectedSpecies,
                    market_comparison_config: config,
                  });
                }}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onClick={onEditClose}>
              Cancelar
            </Button>
            <Button
              color='primary'
              onClick={handleEditSubmit}
            >
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Modal */}
      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDeleteConfirm}
        title='Confirmar Eliminación'
        confirmLabel='Eliminar'
        cancelLabel='Cancelar'
        isDestructive
      >
        <div>
          ¿Está seguro de que desea eliminar {selectedSpecies?.name}?
          <p className='text-sm text-default-500 mt-2'>
            Nota: Las especies que están asociadas a exportadores no pueden
            ser eliminadas.
          </p>
        </div>
      </ConfirmModal>

      {/* New Modal */}
      <Modal
        isOpen={isNewOpen}
        onClose={onNewClose}
        size='4xl'
        scrollBehavior='inside'
      >
        <ModalContent>
          <ModalHeader>Nueva Especie</ModalHeader>
          <ModalBody className='max-h-[75vh] overflow-y-auto'>
            <div className='space-y-4'>
              <Input
                label='Nombre'
                value={newSpecies.name}
                onChange={(e) =>
                  setNewSpecies({ ...newSpecies, name: e.target.value })
                }
              />
              <Input
                label='Nombre en Excel'
                value={newSpecies.excel_name}
                onChange={(e) =>
                  setNewSpecies({ ...newSpecies, excel_name: e.target.value })
                }
              />

              <div className='flex items-center gap-2'>
                <Switch
                  isSelected={newSpecies.has_color}
                  onValueChange={(value) =>
                    setNewSpecies({
                      ...newSpecies,
                      has_color: value,
                    })
                  }
                />
                <span>Tiene color</span>
              </div>

              <div className='grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-4'>
              <div className='space-y-2'>
                <h4 className='font-medium'>Variedades</h4>
                <p className='text-sm text-default-500'>
                  El orden de las variedades es importante.
                </p>

                <div className='flex gap-2'>
                  <Input
                    fullWidth
                    value={newVariety}
                    onChange={(e) => setNewVariety(e.target.value)}
                    placeholder='Nueva variedad'
                  />
                  <Button color='primary' onClick={addVarietyToNew}>
                    <Icon icon='tabler:plus' />
                  </Button>
                </div>

                <VarietiesList
                  varieties={newSpecies.varieties}
                  onRemove={removeVarietyFromNew}
                  onMove={moveNewVariety}
                />
              </div>

              <div className='space-y-2'>
                <h4 className='font-medium'>Calibres</h4>
                <p className='text-sm text-default-500'>
                  El orden de los calibres es importante. Dejar vacío para permitir todos los calibres.
                </p>

                <div className='flex gap-2'>
                  <Input
                    fullWidth
                    value={newCaliber}
                    onChange={(e) => setNewCaliber(e.target.value)}
                    placeholder='Nuevo calibre'
                  />
                  <Button color='primary' onClick={addCaliberToNew}>
                    <Icon icon='tabler:plus' />
                  </Button>
                </div>

                <VarietiesList
                  varieties={newSpecies.calibers}
                  onRemove={removeCaliberFromNew}
                  onMove={moveNewCaliber}
                />
              </div>

              <div className='space-y-2'>
                <h4 className='font-medium'>Colores</h4>
                <p className='text-sm text-default-500'>
                  El orden de los colores es importante. Dejar vacío si no aplica.
                </p>

                <div className='flex gap-2'>
                  <Input
                    fullWidth
                    value={newColor}
                    onChange={(e) => setNewColor(e.target.value)}
                    placeholder='Nuevo color'
                  />
                  <Button color='primary' onClick={addColorToNew}>
                    <Icon icon='tabler:plus' />
                  </Button>
                </div>

                <VarietiesList
                  varieties={newSpecies.colors}
                  onRemove={removeColorFromNew}
                  onMove={moveNewColor}
                />
              </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onClick={onNewClose}>
              Cancelar
            </Button>
            <Button
              color='primary'
              onClick={handleNewSubmit}
            >
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
};

export default SuperadminSpecies;
